<template>
  <app-share-drawer :model-value="documentsPane.isOpen"
                    :title="$t('project.documents.pane.DocumentsShareByEmailPane.shareByEmail')"
                    data-locator="documents-share-by-mail-pane"
                    :loading="shareDocumentsIndexByEmailPending" :confirm-disabled="shareDisabled"
                    @close="close" @confirm="shareByEmail"
  >
    <template v-if="documentsPane.isOpen" #body>
      <div class="d-flex flex-column ga-6">
        <div class="d-flex flex-column ga-2">
          <span class="heading-xs-bold text-text-primary">{{$t('project.documents.pane.DocumentsShareByEmailPane.selectFormat')}}</span>
          <v-radio-group v-model="shareFormat" class="mt-0">
            <v-radio :value="shareFormatOptions.EXCEL" data-locator="excel-pdf-option">
              <template #label>
                <span class="text-text-secondary">{{ $t('project.documents.pane.DocumentsShareByEmailPane.formatExcel') }}</span>
              </template>
            </v-radio>
            <v-radio :value="shareFormatOptions.PDF" data-locator="pdf-index-option">
              <template #label>
                <span class="text-text-secondary">{{ $t('project.documents.pane.DocumentsShareByEmailPane.formatPdf') }}</span>
              </template>
            </v-radio>
          </v-radio-group>
        </div>

        <v-divider />

        <div class="d-flex flex-column ga-2">
          <span class="heading-xs-bold text-text-primary">{{$t('project.documents.pane.DocumentsShareByEmailPane.selectRecipients')}}</span>
          <DocumentSharePicker :forcePmSelection="false" users-only />
        </div>

        <v-divider />

        <div class="d-flex flex-column ga-2">
          <span class="heading-xs-bold text-text-primary">{{$t('project.documents.pane.DocumentsShareByEmailPane.addMessage')}}</span>
          <VisualTextEditor v-model:value="notifyMessage"
                            counter="footer"
                            :error-messages="notifyMessageErrors"
                            :max-length="NOTIFY_MESSAGE_MAX_LENGTH"
                            :placeholder="$t('project.documents.pane.DocumentsShareByEmailPane.optionalMessage')"
          />
        </div>
      </div>
    </template>
  </app-share-drawer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { CLOSE_DOCUMENTS_PANE, SHARE_DOCUMENTS_INDEX_BY_EMAIL } from '@/store/modules/documents/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'DocumentsShareByEmailPane',
  components: { DocumentSharePicker, VisualTextEditor },
  data () {
    return {
      notifyMessage: '',
      shareFormat: '',
      shareFormatOptions: Object.freeze({
        EXCEL: 'documents-index-excel',
        PDF: 'documents-index-pdf',
      }),
      NOTIFY_MESSAGE_MAX_LENGTH,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('documents', ['documentsPane', 'shareDocumentsIndexByEmailPending']),
    ...mapState('documentSharePicker', ['selectedGroups', 'selectedMembers']),
    noRecipientsSelected () {
      return this.selectedGroups.length === 0 && this.selectedMembers.length === 0
    },
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.NOTIFY_MESSAGE_MAX_LENGTH
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.notifyMessageIsTooLong || !this.shareFormat || this.noRecipientsSelected
    },
  },
  watch: {
    // The drawer now lives in this component, which therefore survives its own closing: the
    // format and the message have to be reset by hand. They used to be discarded for free,
    // when the wrapping pane destroyed this component on close.
    'documentsPane.isOpen' (isOpen) {
      if (isOpen) {
        this.notifyMessage = ''
        this.shareFormat = ''
      }
    },
  },
  methods: {
    ...mapActions('documents', [CLOSE_DOCUMENTS_PANE, SHARE_DOCUMENTS_INDEX_BY_EMAIL]),
    close () {
      this.CLOSE_DOCUMENTS_PANE()
    },
    async shareByEmail () {
      const shareData = {
        type: this.shareFormat,
        withUsers: this.selectedMembers.map(i => i.id),
        withGroups: this.selectedGroups.map(i => i.id),
      }

      if (this.notifyMessage) {
        shareData.message = this.notifyMessage
      }

      try {
        await this.SHARE_DOCUMENTS_INDEX_BY_EMAIL({
          mnemo: this.roomMnemo,
          data: shareData,
        })
        toast.success(this.$t('project.documents.pane.DocumentsShareByEmailPane.shareByEmailSuccess'))
        this.CLOSE_DOCUMENTS_PANE()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.pane.DocumentsShareByEmailPane.shareByEmailError'))
      }
    },
  },
}
</script>
